<template>
  <div class="min-h-screen bg-background">
    <!-- Top bar -->
    <header class="sticky top-0 z-50 border-b border-border bg-background">
      <div class="container flex h-14 items-center justify-between">
        <button
          class="flex items-center gap-1 text-sm font-medium text-foreground transition-colors hover:text-primary"
          @click="goBack"
        >
          <Icon name="tabler:chevron-left" size="20" />
          {{ $ts('checkout.back') }}
        </button>
        <div class="flex items-center gap-1.5 text-sm text-muted-foreground">
          <Icon name="tabler:lock" size="14" />
          {{ $ts('checkout.secureCheckout') }}
        </div>
      </div>
    </header>

    <main class="container py-8">
      <!-- Page heading -->
      <div class="mb-8">
        <h1 class="text-2xl font-bold text-foreground">{{ $ts('checkout.title') }}</h1>
        <p class="mt-1 text-muted-foreground">{{ $ts('checkout.subtitle') }}</p>
      </div>

      <!-- Two-column layout -->
      <div class="grid gap-8 lg:grid-cols-[1fr_380px]">
        <!-- Left column: form -->
        <div class="space-y-6">
          <CheckoutQuickAuth @select="handleQuickAuth" />
          <CheckoutContactDetails v-model="contactDetails" />
          <CheckoutPaymentMethod
            v-model="paymentMethod"
            :order-id="orderId"
            @payment-completed="handlePaymentCompleted"
            @payment-failed="handlePaymentFailed"
          />
        </div>

        <!-- Right column: summary sidebar -->
        <div class="self-start lg:sticky lg:top-20">
          <CheckoutSummary
            :partner-name="booking.partnerName"
            :service-name="booking.serviceName"
            :date="booking.date"
            :time="booking.time"
            :duration="booking.duration"
            :specialist-name="booking.specialistName"
            :price="booking.price"
            :currency="booking.currency"
            @confirm="handleConfirm"
            @edit-date-time="handleEditDateTime"
          />
        </div>
      </div>
    </main>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useI18n, useSeoMeta } from '#imports';
import CheckoutQuickAuth from '#components/pages/checkout/CheckoutQuickAuth.vue';
import CheckoutContactDetails from '#components/pages/checkout/CheckoutContactDetails.vue';
import CheckoutPaymentMethod from '#components/pages/checkout/CheckoutPaymentMethod.vue';
import CheckoutSummary from '#components/pages/checkout/CheckoutSummary.vue';
import type { ContactDetails } from '#components/pages/checkout/CheckoutContactDetails.vue';

definePageMeta({
  layout: false,
  indexable: false,
});

const { $ts } = useI18n();
useSeoMeta({ title: () => $ts('checkout.seo.title') });
const router = useRouter();
const route = useRoute();

// ── Order ID from route query (passed from booking flow) ────────────────────
const orderId = computed(() => (route.query.orderId as string) || undefined);

// ── Form state ──────────────────────────────────────────────────────────────
const contactDetails = ref<ContactDetails>({
  fullName: '',
  email: '',
  phone: '',
  specialRequests: '',
});

const paymentMethod = ref<'online' | 'venue'>('online');
const paymentStatus = ref<'idle' | 'processing' | 'completed' | 'failed'>('idle');

// ── Placeholder booking data (will come from route query / store) ───────────
const booking = ref({
  partnerName: 'Happy Paws Grooming Studio',
  serviceName: 'Full Grooming - Small Dog',
  date: new Date(2026, 1, 7), // Feb 7, 2026
  time: '10:30 AM',
  duration: 1.5,
  specialistName: 'Emma Johnson',
  price: 35,
  currency: 'EUR',
});

// ── Handlers ────────────────────────────────────────────────────────────────
function goBack() {
  router.back();
}

function handleQuickAuth(provider: string) {
  // TODO: Implement social auth flow
}

function handlePaymentCompleted(resultCode: string) {
  paymentStatus.value = 'completed';
  // Navigate to confirmation page with the result
  router.push({
    path: '/checkout/result',
    query: {
      orderId: orderId.value,
      resultCode,
      status: 'success',
    },
  });
}

function handlePaymentFailed(resultCode: string) {
  paymentStatus.value = 'failed';
  // Could show an error message or navigate to a failure page
  router.push({
    path: '/checkout/result',
    query: {
      orderId: orderId.value,
      resultCode,
      status: 'failed',
    },
  });
}

function handleConfirm() {
  if (paymentMethod.value === 'venue') {
    // "Pay at venue" — no online payment needed, just confirm the booking
    paymentStatus.value = 'completed';
    router.push({
      path: '/checkout/result',
      query: {
        orderId: orderId.value,
        status: 'venue',
      },
    });
  }
  // If paymentMethod is 'card', Adyen Drop-in handles submission directly
}

function handleEditDateTime() {
  router.back();
}
</script>
